import { ComponentProps, FC } from "react";
import { capitalize } from "lodash";
import { useTranslation } from "@vixi/localization";
import { Button, cn, Img } from "@vixi/ui";

// Assets
import ChevronRightCircle from "@assets/icons/chevron-right-circle.svg?react";
import cross_circle_solid_sharp from "@assets/icons/cross-circle-solid-sharp.png";
import check_circle_solid_sharp from "@assets/icons/check-circle-solid-sharp.png";
import { PaymentPlan, SubscriptionType } from "@vixi/types";

interface PaymentPlanSummarySlotProps extends ComponentProps<"div"> {
  isFree: boolean;
}

const slotShouldForwardProp = (prop: PropertyKey) => prop !== "isFree";

const PaymentPlanSummaryRoot = ({ isFree, ...props }: PaymentPlanSummarySlotProps) => (
  <div
    {...props}
    className={cn(
      "bg-paper relative w-73.75 rounded border-2 pb-8.75 overflow-hidden",
      isFree ? "border-primary-light" : "border-premium",
      props.className,
    )}
  />
);

const PaymentPlanSummaryTitle = ({ isFree, ...props }: PaymentPlanSummarySlotProps) => (
  <h2
    {...props}
    className={cn(
      "fs-25   block min-w-37.5   p-2 text-center font-bold capitalize",
      isFree ? "border-primary-light text-primary" : "border-premium text-premium",
      props.className,
    )}
  />
);

const PaymentCostHeader = (props: ComponentProps<"h2">) => (
  <h2
    {...props}
    className={cn("fs-34 text-center leading-[1.2] font-bold text-black", props.className)}
  />
);

const PaymentIntervalSubheader = (props: ComponentProps<"span">) => (
  <span
    {...props}
    className={cn("fs-20 leading-inherit fw-inherit text-current", props.className)}
  />
);

const PaymentBillInterval = (props: ComponentProps<"p">) => (
  <p
    {...props}
    className={cn(
      "fs-12 leading-inherit text-end font-light text-current lowercase",
      props.className,
    )}
  />
);

interface PaymentPlanSummaryProps {
  plan: PaymentPlan;
  isLoading?: boolean;
  selectedSubscriptionType: SubscriptionType;
  onSwitchPlan: (plan: PaymentPlan, isFree: boolean) => void;
}

export const PaymentPlanSummary: FC<PaymentPlanSummaryProps> = ({
  plan,
  isLoading,
  selectedSubscriptionType,
  onSwitchPlan,
}) => {
  const { t, byLocale } = useTranslation();

  const planPrice =
    plan[`${selectedSubscriptionType.name}Price`] /
    (selectedSubscriptionType.name === "yearly" ? 12 : 1);
  const isFree = planPrice === 0;

  // Choose plan image based on plan name (Free / Plus / Pro)
  const planLabel = byLocale(plan.title);
  // Fallback to plan.title.en if planLabel doesn't match translations
  const planTitleEn = plan.title.en?.toLowerCase() || "";
  
  const planImageSrc =
    planLabel === t("Premium.Plans.Free") || planTitleEn === "free"
      ? "/images/free.webp"
      : planLabel === t("Premium.Plans.Pro") || planTitleEn === "pro"
        ? "/images/plusss.webp"
        : planLabel === t("Premium.Plans.Premium") || planTitleEn === "premium"
          ? "/images/pro.webp"
          : planTitleEn === "plus"
            ? "/images/pro.webp"
            : "";

  return (
    <PaymentPlanSummaryRoot isFree={isFree}>
      <div
        className={cn(
          "flex w-full justify-center items-center rounded-2xl px-3 py-2.5",
          isFree ? "bg-[#FFF8F3]" : "bg-[#F8F2FF]",
        )}
      >
        <Img contain src={planImageSrc} alt="" className="h-[158px] w-[158px]" />
        <PaymentPlanSummaryTitle isFree={isFree}>{planLabel}</PaymentPlanSummaryTitle>
      </div>

      <div className="m-auto w-35 min-w-max px-6.25">
        <PaymentCostHeader className="mt-8.75">
          {planPrice?.toFixed(2)} {plan.currency}
          <PaymentIntervalSubheader>/{t("Premium.Mo")}</PaymentIntervalSubheader>
        </PaymentCostHeader>

        <PaymentBillInterval {...(isFree && { sx: { opacity: 0, pointerEvents: "none" } })}>
          {t(`Premium.Billed${capitalize(selectedSubscriptionType.name)}`)}
        </PaymentBillInterval>
      </div>

      <div className="mt-3.75 px-6.25">
        <Button
          variant="contained"
          startIcon={<ChevronRightCircle />}
          size="small"
          disabled={isLoading}
          onClick={() => !plan.inPlan && onSwitchPlan(plan, isFree)}
          color={isFree ? "primary" : "premium"}
          className={cn("w-full", plan.inPlan ? "pointer-events-none" : "")}
        >
          {plan.inPlan
            ? t("Premium.YourCurrentPlan")
            : isFree
              ? t("Action.CancelSubscription")
              : `${t("Action.SwitchTo")} ${byLocale(plan.title)}`}
        </Button>
      </div>

      <div className="mt-7 flex flex-col gap-4 px-6.25">
        {plan.features.map((feature) => (
          <div key={feature.title.en}>
            <div className="mb-0.5 flex gap-[1ch]">
              <p className="font-semibold text-black">{byLocale(feature.title)}</p>
              {feature.comingSoon && (
                <p className="fs-11 text-premium mb-0.75 self-end font-semibold">
                  {t("Premium.ComingSoon")}...
                </p>
              )}
            </div>

            <ul className="list-none space-y-1 ps-0">
              {feature.categories.map((category, i) => (
                <li className="flex" key={i}>
                  <span className="flex min-w-12 justify-center">
                    <Img
                      contain
                      src={category.available ? check_circle_solid_sharp : cross_circle_solid_sharp}
                      className="mx-1.5 h-auto w-6"
                    />
                  </span>
                  <span className="fs-14 text-black">{byLocale(category.text)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </PaymentPlanSummaryRoot>
  );
};
